/**
 * StatCard Component
 * Reusable metric/stat card with gradient background
 *
 * Usage:
 * <StatCard
 *     label="Total Properties"
 *     value={150}
 *     icon={<Building2 size={20} />}
 *     variant="primary"
 * />
 */

import { ReactNode } from 'react';

type StatCardVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'dark';

interface StatCardProps {
    label: string;
    value: string | number;
    icon?: ReactNode;
    variant?: StatCardVariant;
    loading?: boolean;
    className?: string;
    /** One short line under the value that qualifies it ("30 shown on the site"). */
    detail?: string;
    trend?: {
        value: number;
        label?: string;
    };
}

const VARIANT_GRADIENTS: Record<StatCardVariant, string> = {
    primary: 'bg-gradient-to-r from-primary-dark to-primary',
    secondary: 'bg-gradient-to-r from-primary to-primary-light',
    success: 'bg-gradient-to-r from-green-600 to-green-500',
    warning: 'bg-gradient-to-r from-amber-600 to-amber-500',
    danger: 'bg-gradient-to-r from-red-600 to-red-500',
    info: 'bg-gradient-to-r from-blue-600 to-blue-500',
    // Brand charcoal, not gray-800/700: the Century 21 Dark palette inverts the
    // grays, which would turn this "dark" card light under white text.
    dark: 'bg-gradient-to-r from-secondary to-secondary-light',
};

export function StatCard({
    label,
    value,
    icon,
    variant = 'primary',
    loading = false,
    className = '',
    detail,
    trend,
}: StatCardProps) {
    const gradient = VARIANT_GRADIENTS[variant];

    return (
        <div
            className={`
                ${gradient} rounded-xl p-6 text-white
                transform hover:scale-105 transition-all duration-300
                hover:shadow-xl ${className}
            `.trim()}
        >
            <div className="flex items-center justify-between mb-3">
                <span className="text-white/80 font-medium">{label}</span>
                {icon && (
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        {icon}
                    </div>
                )}
            </div>
            <div className="text-3xl font-bold">
                {loading ? (
                    <div className="h-9 w-20 bg-white/20 rounded animate-pulse" />
                ) : (
                    value
                )}
            </div>
            {detail && !loading && (
                <p className="mt-1 text-sm text-white/70">{detail}</p>
            )}
            {trend && !loading && (
                <div className="mt-2 flex items-center gap-1 text-sm">
                    <span className={trend.value >= 0 ? 'text-green-300' : 'text-red-300'}>
                        {trend.value >= 0 ? '+' : ''}{trend.value}%
                    </span>
                    {trend.label && (
                        <span className="text-white/60">{trend.label}</span>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * StatCardGrid - Grid wrapper for StatCards
 */
interface StatCardGridProps {
    children: ReactNode;
    columns?: 2 | 3 | 4;
    className?: string;
}

export function StatCardGrid({ children, columns = 4, className = '' }: StatCardGridProps) {
    const gridCols = {
        2: 'sm:grid-cols-2',
        3: 'sm:grid-cols-2 lg:grid-cols-3',
        4: 'sm:grid-cols-2 lg:grid-cols-4',
    };

    return (
        <div className={`grid grid-cols-1 ${gridCols[columns]} gap-4 ${className}`}>
            {children}
        </div>
    );
}

/**
 * SimpleStatCard - Simpler stat card without gradient (for secondary stats)
 */
interface SimpleStatCardProps {
    label: string;
    value: string | number;
    className?: string;
    /** One short line under the label that qualifies the value. */
    detail?: string;
}

export function SimpleStatCard({ label, value, className = '', detail }: SimpleStatCardProps) {
    return (
        <div className={`text-center p-4 bg-gray-50 rounded-lg ${className}`}>
            <div className="text-2xl font-bold text-primary">{value}</div>
            <div className="text-sm text-gray-500">{label}</div>
            {detail && <div className="mt-1 text-xs text-gray-600">{detail}</div>}
        </div>
    );
}

export default StatCard;
